import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, DeviceMobile } from '@phosphor-icons/react';

/**
 * Prominent Install Banner - Shows at top of app
 */
const InstallBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Check if user has dismissed recently
      const dismissed = localStorage.getItem('installBannerDismissed');
      if (dismissed) {
        const dismissedTime = parseInt(dismissed);
        const threeDays = 3 * 24 * 60 * 60 * 1000;
        if (Date.now() - dismissedTime < threeDays) {
          return;
        }
      }
      
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User installed the app');
    }

    setDeferredPrompt(null);
    setShowBanner(false);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('installBannerDismissed', Date.now().toString());
  };

  if (!showBanner || !deferredPrompt) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -100, opacity: 0 }}
        className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-gold via-gold-light to-gold p-4 shadow-2xl"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          {/* Icon */}
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex w-12 h-12 rounded-full bg-black/20 items-center justify-center">
              <DeviceMobile size={28} className="text-black" weight="duotone" />
            </div>
            
            {/* Message */}
            <div>
              <h3 className="font-unbounded font-bold text-black text-lg">
                Install TeenVest Now!
              </h3>
              <p className="text-sm text-black/80">
                Get instant access - works like a real app on your phone or computer
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleInstall}
              className="flex items-center gap-2 px-6 py-3 bg-black text-gold font-bold rounded-lg hover:bg-black/90 transition-all shadow-lg whitespace-nowrap"
            >
              <Download size={20} weight="fill" />
              <span className="hidden sm:inline">Install Now</span>
              <span className="sm:hidden">Install</span>
            </button>
            
            <button
              onClick={handleDismiss}
              className="p-2 hover:bg-black/10 rounded-lg transition-colors"
              aria-label="Dismiss"
            >
              <X size={24} className="text-black" weight="bold" />
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InstallBanner;
